import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, partition } from 'rxjs';
import { delayWhen, filter, finalize, map, repeatWhen, takeUntil } from 'rxjs/operators';

import {
  AuthUsecase,
  DistinctSubject,
  NeverError,
  Period,
  PeriodSubject,
  UserTimezone,
  WebSocketSyncData,
  WebSocketUsecase,
  recursiveQuery,
} from '@daikin-tic/dxone-com-lib';

import { Project, ProjectQueryParams, Projects } from '../models/project.model';
import { Unit } from '../models/unit.model';
import { ProjectGateway } from '../usecases/project.gateway';
import { ProjectUsecase } from '../usecases/project.usecase';

@Injectable()
export class ProjectInteractor extends ProjectUsecase {
  get projects$(): Observable<Projects> {
    return this._projects;
  }
  get period$(): Observable<Period> {
    return this._period;
  }
  get unitUpdateNotice$(): Observable<void> {
    return this._unitUpdateNotice;
  }

  private readonly _projects = new DistinctSubject<Projects>(new Projects());
  private readonly _period = new PeriodSubject({ from: -1, to: -1 });
  private readonly _unitUpdateNotice = new BehaviorSubject<void>(void 0);

  constructor(
    private _authUsecase: AuthUsecase,
    private _webSocketUsecase: WebSocketUsecase,
    private _projectGateway: ProjectGateway,
  ) {
    super();
    this._authUsecase.authState$.pipe(filter(({ status }) => status === 'signedIn')).subscribe(() => {
      this._period.now();
    });

    const [open$, close$] = partition(this._webSocketUsecase.isOpen$, isOpen => isOpen);
    this.period$
      .pipe(
        filter(({ from, to }) => from >= 0 && to >= 0),
        takeUntil(close$),
        finalize(() => this._projects.next(new Projects())),
        repeatWhen(notifications => notifications.pipe(delayWhen(() => open$))),
      )
      .subscribe(({ from, to }) => this.listProjects(from, to));

    this._webSocketUsecase.isOpen$.subscribe(isOpen => (isOpen ? this.onSignIn() : this.onSignOut()));
    this._webSocketUsecase.message$
      .pipe(
        filter(message => message.action === 'sync' && message.data?.source === 'project'),
        map(({ data }) => data as WebSocketSyncData<Project>),
      )
      .subscribe(data => {
        switch (data.reason) {
          case 'create':
          case 'update':
            this._projects.next(this._projects.value.set(data.payload as Project));
            break;
          case 'delete': {
            this._projects.next(this._projects.value.delete((data.payload as Project).projectId));
            break;
          }
          default:
            throw new NeverError(data.reason);
        }
      });
    this._webSocketUsecase.message$
      .pipe(
        filter(message => message.action === 'sync' && message.data?.source === 'unit'),
        map(({ data }) => data as WebSocketSyncData<Unit>),
      )
      .subscribe(data => {
        const unit = data.payload as Unit;
        switch (data.reason) {
          case 'create':
            this.onCreateUnit(unit);
            break;
          case 'update':
            this.onUpdateUnit(unit);
            break;
          case 'delete':
            this.onDeleteUnit(unit.projectId, unit.unitId);
            break;
          default:
            throw new NeverError(data.reason);
        }
      });
  }

  changeTimezone(timezone: UserTimezone): void {
    this._period.tz(timezone);
  }

  changePeriod(period: Period): void {
    this._period.next(period);
  }

  private onCreateUnit(createdUnit: Unit): void {
    const project = this._projects.value.get(createdUnit.projectId);
    if (project && !project.units?.find(({ unitId }) => unitId === createdUnit.unitId)) {
      project.units = [...(project.units || []), createdUnit];
      this._projects.next(this._projects.value.set(project));
      this._unitUpdateNotice.next();
    }
  }

  private onUpdateUnit(updatedUnit: Unit): void {
    const project = this._projects.value.get(updatedUnit.projectId);
    if (project && project.units?.find(({ unitId, version }) => unitId === updatedUnit.unitId && version < updatedUnit.version)) {
      project.units = project.units?.reduce((acc, cur) => {
        const unit = cur.unitId === updatedUnit.unitId ? updatedUnit : cur;
        return [...acc, unit];
      }, [] as Unit[]);
      this._projects.next(this._projects.value.set(project));
      this._unitUpdateNotice.next();
    }
  }

  private onDeleteUnit(projectId: string, unitId: string): void {
    const project = this._projects.value.get(projectId);
    if (project) {
      project.units = project.units?.reduce((acc, cur) => {
        return cur.unitId === unitId ? acc : [...acc, cur];
      }, [] as Unit[]);
      this._projects.next(this._projects.value.set(project));
      this._unitUpdateNotice.next();
    }
  }

  private listProjects(from: number, to: number): void {
    const queryParams = {
      scheduledFrom: from.toString(),
      scheduledTo: to.toString(),
    } as ProjectQueryParams;
    recursiveQuery(params => this._projectGateway.listProjects(params), queryParams).subscribe(projects => {
      this._projects.next(new Projects(projects));
    });
  }

  private onSignIn(): void {
    const { from, to } = this._period.value;
    this.listProjects(from, to);
  }

  private onSignOut(): void {
    this._projects.next(new Projects());
  }
}
